/**
 * LocalizedMonetaryInput — the money entry control (DECIMAL dollars, never cents).
 *
 * Precision follows the money type policy in docs/erp/modern-architecture-principles.md:
 * an *amount* is NUMERIC(18,2) and an *FX rate / per-unit price or rate* is
 * NUMERIC(18,4). The default here is 2 because most fields are amounts; a field
 * whose backing column is NUMERIC(18,4) MUST pass `decimalPlaces={4}` or the
 * blur-reformat and the value-sync effect will render a legitimate sub-cent value
 * (0.0037) as "0.00" — the user cannot verify or re-edit it, which reads as data
 * loss. See the `decimalPlaces` prop doc.
 *
 * `decimalPlaces={4}` means **up to** 4 — the read/write twin of
 * `LocalizedCurrencyDisplay`'s prop, and for the same reason: a unit price is a
 * plain two-decimal number in the vast majority of cases, so padding every one of
 * them to "12.3400" claims a precision the value does not have and makes a line
 * editor tiring to read. The sub-cent case still round-trips intact.
 */

import React from 'react';
import { cn } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { NumericFieldBox } from './common/NumericFieldBox';
import { useUserPreferences } from '../context';
import { useState, useCallback, useEffect } from 'react';

export interface LocalizedMonetaryInputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  currency?: string;
  currencyPosition?: 'start' | 'end';
  allowNegative?: boolean;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  id?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  min?: number;
  max?: number;
  autoFocus?: boolean;
  /**
   * Decimals shown (and preserved across blur / re-render). Match the backing
   * column: 2 for a NUMERIC(18,2) amount, 4 for a NUMERIC(18,4) per-unit price
   * or rate. Defaults to 2 — the amount case.
   *
   * `4` means **up to** 4, floored at 2, so trailing zeros are suppressed: a
   * 12.34 unit price shows "12.34" and only a value with real precision beyond
   * the cent (12.3456, 0.0037) spends the extra places. `2` still means exactly
   * two. This mirrors `LocalizedCurrencyDisplay`'s prop of the same name so a
   * value reads back exactly as it was entered.
   */
  decimalPlaces?: 2 | 4;
  /** Accessible name when the field has no visible <Label> (e.g. a dense grid row). */
  'aria-label'?: string;
  'data-testid'?: string;
}

export function LocalizedMonetaryInput({
  value,
  onChange,
  currency,
  currencyPosition = 'start',
  allowNegative = false,
  label,
  placeholder,
  helperText,
  error,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
  id,
  onBlur: externalOnBlur,
  min: minProp,
  max,
  autoFocus,
  decimalPlaces = 2,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: LocalizedMonetaryInputProps) {
  const { formatNumber, parseNumber, formatOptions } = useUserPreferences();
  const generatedId = React.useId();

  const decimalSeparator = formatOptions.numberFormat === 'dot_comma' ? ',' : '.';
  const thousandSeparator = formatOptions.numberFormat === 'dot_comma' ? '.' : ',';
  const min = minProp !== undefined ? minProp : (allowNegative ? undefined : 0);

  // Format number for display
  const formatForDisplay = useCallback((num: number | null): string => {
    if (num === null || num === undefined) return '';

    // A 4-dp column is "up to 4, floored at 2": most unit prices are plain
    // two-decimal money and padding them to "12.3400" is noise, but a genuine
    // sub-cent value must survive the round-trip.
    return formatNumber(num, {
      minimumFractionDigits: Math.min(decimalPlaces, 2),
      maximumFractionDigits: decimalPlaces,
    });
  }, [formatNumber, decimalPlaces]);

  const [displayValue, setDisplayValue] = useState(() => formatForDisplay(value));
  const [isFocused, setIsFocused] = useState(false);
  // True once the user has typed into the field during the CURRENT focus. Focus
  // alone is not enough to block the value sync: Radix autofocuses a dialog's
  // first field as it opens, so the real value lands a tick AFTER focus and a
  // focus-only guard left the field showing the placeholder until the user
  // clicked away. Only an actual keystroke owns the text.
  const hasTypedRef = React.useRef(false);

  const clampToBounds = useCallback((num: number): number => {
    if (min !== undefined && num < min) return min;
    if (max !== undefined && num > max) return max;
    return num;
  }, [min, max]);

  // Update display when the external value changes, but NOT over what the user
  // is actively typing.
  useEffect(() => {
    if (isFocused && hasTypedRef.current) return;
    const currentParsed = parseNumber(displayValue);
    if (value !== currentParsed) {
      setDisplayValue(formatForDisplay(value));
    }
  }, [value, parseNumber, formatForDisplay, displayValue, isFocused]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    hasTypedRef.current = true;

    // Allow empty input
    if (input === '') {
      setDisplayValue('');
      onChange(null);
      return;
    }

    // Build valid characters regex
    const validChars = allowNegative
      ? `[0-9${decimalSeparator}${thousandSeparator}\\-]`
      : `[0-9${decimalSeparator}${thousandSeparator}]`;
    const cleanedInput = input.replace(new RegExp(`[^${validChars.slice(1, -1)}]`, 'g'), '');

    // Prevent multiple decimal separators
    const decimalCount = (cleanedInput.match(new RegExp(`\\${decimalSeparator}`, 'g')) || []).length;
    if (decimalCount > 1) return;

    // Prevent multiple negatives or negative not at start
    if (allowNegative) {
      const negativeCount = (cleanedInput.match(/-/g) || []).length;
      if (negativeCount > 1) return;
      if (negativeCount === 1 && cleanedInput.indexOf('-') !== 0) return;
    }

    const parsed = parseNumber(cleanedInput);
    if (parsed === null) {
      setDisplayValue(cleanedInput);
      return;
    }

    // A POSITIVE entry above the ceiling shows the ceiling, because no further
    // digit brings it back into range; everything else — a below-minimum entry,
    // or a negative one heading further down towards a negative maximum — is a
    // prefix and waits for settle(). See LocalizedNumericInput.handleChange for
    // the full contract, including why the emitted value is clamped from the
    // first keystroke.
    const constrained = clampToBounds(parsed);
    const clampedByMax = max !== undefined && parsed > max && parsed > 0;
    setDisplayValue(clampedByMax ? formatForDisplay(constrained) : cleanedInput);
    onChange(constrained);
  }, [allowNegative, decimalSeparator, thousandSeparator, max, clampToBounds, formatForDisplay, onChange, parseNumber]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    // A fresh focus owns no text yet — an externally-arriving value may still land.
    hasTypedRef.current = false;
  }, []);

  /**
   * Blur and Enter SETTLE the field: the display becomes the value that was
   * emitted. See LocalizedNumericInput.settle for the full contract.
   */
  const settle = useCallback(() => {
    if (!hasTypedRef.current) return;

    const parsed = parseNumber(displayValue);
    if (parsed === null) {
      if (displayValue !== '') {
        setDisplayValue('');
        onChange(null);
      }
      return;
    }

    const constrained = clampToBounds(parsed);
    setDisplayValue(formatForDisplay(constrained));
    if (constrained !== parsed) onChange(constrained);
  }, [displayValue, parseNumber, formatForDisplay, clampToBounds, onChange]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    settle();

    externalOnBlur?.(e);
  }, [settle, externalOnBlur]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    // Enter commits the surrounding form without ever blurring the field.
    if (e.key === 'Enter') settle();
  }, [settle]);

  const inputId = id || `monetary-input-${generatedId}`;

  const showCurrencyStart = currency && currencyPosition === 'start';
  const showCurrencyEnd = currency && currencyPosition === 'end';

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <div className="mb-1.5 leading-none">
          <Label htmlFor={inputId} required={required}>
            {label}
          </Label>
        </div>
      )}
      {/* Control box pinned LTR: the currency affix is positioned with the
          logical `start-3`/`end-3`, which under an RTL document resolve onto
          the LTR-aligned digits. See NumericFieldBox. */}
      <NumericFieldBox className="relative">
        {showCurrencyStart && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
            {currency}
          </span>
        )}
        <Input
          id={inputId}
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          aria-label={ariaLabel}
          data-testid={dataTestId}
          inputMode="decimal"
          size={size}
          className={cn(
            showCurrencyStart && 'ps-10',
            showCurrencyEnd && 'pe-10',
            error && 'border-destructive focus-visible:ring-destructive'
          )}
        />
        {showCurrencyEnd && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
            {currency}
          </span>
        )}
      </NumericFieldBox>
      {helperText && (
        <p className={cn(
          'text-xs mt-1',
          error ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {helperText}
        </p>
      )}
    </div>
  );
}
